// Freezing a Builder pivot into a JSON-safe snapshot, and reviving it for the
// viewer. The dashboard stores RESULTS, so a piece keeps exactly what its
// renderer needs — never the raw records or the EQL query.
//
//   chart     -> { data: [{label,count}], type, title, valueMode }
//   table     -> { pivot: <serialized>, valueMode, fieldTitles }
//   indicator -> { value, valueMode }   (a measure pivot freezes "12.3 d")
//
// pivotToChartData / the pivot model are reused verbatim from the Builder so a
// frozen piece looks identical to the live one it was captured from.

import {
  dropZeroPoints,
  pivotToChartData,
} from "../../../../components/Exports/Builder/utils/pivotToChartData";
import { formatDays } from "../../../../components/Exports/Builder/utils/measures";

const mapEntries = (m) => [...m.entries()];

/** Maps aren't JSON-safe; store them as entry arrays. */
export const serializePivot = (p) =>
  p && {
    rowFields: p.rowFields,
    colFields: p.colFields,
    rowKeys: p.rowKeys,
    colKeys: p.colKeys,
    cells: mapEntries(p.cells),
    rowTotals: mapEntries(p.rowTotals),
    colTotals: mapEntries(p.colTotals),
    grandTotal: p.grandTotal,
    isAllRow: p.isAllRow,
    isAllCol: p.isAllCol,
    // Duration measure pivots only (see computePivot).
    ...(p.measure && {
      measure: p.measure,
      reportTotal: p.reportTotal,
      counts: {
        cells: mapEntries(p.counts.cells),
        rowTotals: mapEntries(p.counts.rowTotals),
        colTotals: mapEntries(p.counts.colTotals),
        grandTotal: p.counts.grandTotal,
      },
    }),
  };

/** Inverse of serializePivot — revives the Maps PivotGrid consumes. */
export const deserializePivot = (s) =>
  s && {
    ...s,
    cells: new Map(s.cells || []),
    rowTotals: new Map(s.rowTotals || []),
    colTotals: new Map(s.colTotals || []),
    ...(s.counts && {
      counts: {
        cells: new Map(s.counts.cells || []),
        rowTotals: new Map(s.counts.rowTotals || []),
        colTotals: new Map(s.counts.colTotals || []),
        grandTotal: s.counts.grandTotal,
      },
    }),
  };

// An indicator can read any single total from the pivot: the grand total, a
// row total, a column total, or one cell. Metric keys are stable strings:
//   "grand" | "row:<rowKey>" | "col:<colKey>" | "cell:<rowKey>||<colKey>"

const splitMetric = (key) => {
  const i = key.indexOf(":");
  return i < 0 ? [key, ""] : [key.slice(0, i), key.slice(i + 1)];
};

/**
 * Selectable indicator metrics for a materialized pivot: the grand total plus
 * each row and column TOTAL (individual cells are intentionally excluded — an
 * indicator shows a total, not an arbitrary cross-section).
 */
export const pivotMetricOptions = (pivot) => {
  if (!pivot) return [];
  const opts = [{ value: "grand", label: "Total general" }];
  const rowLbl = (r) => r.values.join(" ▸ ");
  const colLbl = (c) => c.values.join(" ▸ ");

  if (!pivot.isAllRow) {
    pivot.rowKeys.forEach((r) =>
      opts.push({ value: `row:${r.key}`, label: `${rowLbl(r)} (fila)` })
    );
  }
  if (!pivot.isAllCol) {
    pivot.colKeys.forEach((c) =>
      opts.push({ value: `col:${c.key}`, label: `${colLbl(c)} (columna)` })
    );
  }
  return opts;
};

/**
 * Resolves a metric key to its numeric value against the pivot. A measure
 * value with no data (no report completed the stage) is null.
 */
export const pivotMetricValue = (pivot, key = "grand") => {
  if (!pivot) return 0;
  const empty = pivot.measure ? null : 0;
  const [type, rest] = splitMetric(key);
  switch (type) {
    case "row":
      return pivot.rowTotals.get(rest) ?? empty;
    case "col":
      return pivot.colTotals.get(rest) ?? empty;
    case "cell":
      return pivot.cells.get(rest) ?? empty;
    default:
      return pivot.grandTotal ?? empty;
  }
};

/** Display text for a metric: "1,234" for a count, "12.3 d" (or "—") for a measure. */
export const pivotMetricText = (pivot, key) => {
  const value = pivotMetricValue(pivot, key);
  if (pivot?.measure) return formatDays(value) || "—";
  return (value || 0).toLocaleString();
};

/**
 * Builds the frozen `result` for a piece from a materialized pivot.
 * @param {Object} pivot  from materializeView()
 * @param {Object} opts   { kind, chartType, seriesKey, valueMode, title,
 *                          fieldTitles, metricKey, metricLabel }
 */
export const freezePieceResult = (pivot, opts = {}) => {
  const {
    kind,
    chartType,
    seriesKey,
    valueMode,
    title,
    fieldTitles,
    metricKey,
    metricLabel,
  } = opts;
  if (!pivot) return null;

  if (kind === "indicator") {
    return {
      // Days are frozen as text so the viewer shows the unit; RenderPiece
      // prints non-numeric values as-is.
      value: pivot.measure
        ? pivotMetricText(pivot, metricKey)
        : pivotMetricValue(pivot, metricKey),
      label: metricLabel,
      valueMode,
    };
  }
  if (kind === "table") {
    return { pivot: serializePivot(pivot), valueMode, fieldTitles };
  }
  // chart (default)
  return {
    // Zero points are noise on a dashboard (see dropZeroPoints).
    data: dropZeroPoints(pivotToChartData(pivot, seriesKey, valueMode)),
    type: chartType,
    title,
    valueMode,
  };
};
